<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcContractTerms',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    contract: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const filterItems = (...args) => {
    return _.sortBy(args[0], (p) => (isTermStale(p) ? '_' + p.name : p.name));
  };
  const isTermStale = (...args) => {
    const [term] = args;
    return term.valid_to && $fn.value.parseISODate(term.valid_to) < today.value;
  };

  const today = ref(null);
  const displayHelper = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    today: today.value,
    displayHelper: displayHelper.value,
    ux: ux.value,
  }));

  const $$load_today = () => {
    return new Date();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'today' }, { name: 'displayHelper' }, { name: 'ux' }],
    loaders: { today: $$load_today },
    ux: {},
    dataRefs: { today, displayHelper, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.contract,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contract');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );

  const act_2095089031469157_emit_l7qo9xg6o_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_2095089031469157_close_b55r47u0x_getWhen = async ($event) => {
    return !!$event.data.modalResult.ok;
  };
  const act_2095089031469157_showModal_mqf6xpf4d_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ContractTermEditor',
      props: {
        mode: 'add',
        contract: props.contract,
      },
    };
  };
  const act_3577443900630089_emit_56eu1kxzv_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_3577443900630089_close_6ojvyybbc_getWhen = async ($event) => {
    return !!$event.data.modalResult.ok;
  };
  const act_3577443900630089_showModal_s2nkib1lj_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ContractTermEditor',
      props: {
        mode: 'edit',
        contract: props.contract,
        term: _.cloneDeep($event.scope.term),
      },
    };
  };

  const act_7315092382398562_reloadComponentData_sc31sgdoz =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_epugy58vk =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_sc31sgdoz,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_epugy58vk],
    event: 'reload',
  });
  const act_2095089031469157_emit_l7qo9xg6o = new actionTypes.EmitAction({
    actionArgs: act_2095089031469157_emit_l7qo9xg6o_getActionArgs,
    displayName: 'emitReload',
  });
  const inst_evh_2095089031469157_close_b55r47u0x =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_emit_l7qo9xg6o,
      event: 'close',
      when: inst_evh_2095089031469157_close_b55r47u0x_getWhen,
      displayName: 'emitReload',
    });
  const act_2095089031469157_showModal_mqf6xpf4d =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_mqf6xpf4d_getActionArgs,
      events: [inst_evh_2095089031469157_close_b55r47u0x],
    });
  const inst_evh_2095089031469157_clickPreventStop_a6mc2yeva =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_mqf6xpf4d,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_2095089031469157_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2095089031469157_clickPreventStop_a6mc2yeva],
      event: 'click.prevent.stop',
    });
  const act_3577443900630089_emit_56eu1kxzv = new actionTypes.EmitAction({
    actionArgs: act_3577443900630089_emit_56eu1kxzv_getActionArgs,
    displayName: 'emitReload',
  });
  const inst_evh_3577443900630089_close_6ojvyybbc =
    new actionTypes.EventHandlerImpl({
      action: act_3577443900630089_emit_56eu1kxzv,
      event: 'close',
      when: inst_evh_3577443900630089_close_6ojvyybbc_getWhen,
      displayName: 'emitReload',
    });
  const act_3577443900630089_showModal_s2nkib1lj =
    new actionTypes.ShowModalAction({
      actionArgs: act_3577443900630089_showModal_s2nkib1lj_getActionArgs,
      events: [inst_evh_3577443900630089_close_6ojvyybbc],
    });
  const inst_evh_3577443900630089_clickPreventStop_c3jg5jjm8 =
    new actionTypes.EventHandlerImpl({
      action: act_3577443900630089_showModal_s2nkib1lj,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_3577443900630089_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3577443900630089_clickPreventStop_c3jg5jjm8],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_reload_epugy58vk,
    inst_evh_7315092382398562_reload,
    inst_evh_2095089031469157_close_b55r47u0x,
    emitReload: inst_evh_2095089031469157_close_b55r47u0x,
    inst_evh_2095089031469157_clickPreventStop_a6mc2yeva,
    inst_evh_2095089031469157_clickPreventStop,
    inst_evh_3577443900630089_close_6ojvyybbc,
    inst_evh_3577443900630089_clickPreventStop_c3jg5jjm8,
    inst_evh_3577443900630089_clickPreventStop,
  };

  const evh_7315092382398562_reload_epugy58vk = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_epugy58vk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_close_b55r47u0x = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_close_b55r47u0x.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop_a6mc2yeva = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop_a6mc2yeva.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3577443900630089_close_6ojvyybbc = async (event, scope) => {
    return await actions.inst_evh_3577443900630089_close_6ojvyybbc.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3577443900630089_clickPreventStop_c3jg5jjm8 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3577443900630089_clickPreventStop_c3jg5jjm8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3577443900630089_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_3577443900630089_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpWidgetCard>
      <template #header>
        <ItpBox alignItems="center">
          <strong>
            {{ pgettext('Contract', 'Terms') }}
          </strong>
          <ItpButton
            :text="pgettext('Contract', 'Add term...')"
            variant="light"
            size="sm"
            class="ms-auto"
            v-if="!readonly"
            @click.prevent.stop="
              evh_2095089031469157_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </template>
      <BListGroup>
        <BListGroupItem v-if="!contract.terms || !contract.terms.length">
          <ItpText>
            {{ pgettext('Contract', 'No terms have been added yet.') }}
          </ItpText>
        </BListGroupItem>
        <BListGroupItem
          :key="term.id"
          v-for="term in filterItems(contract.terms)"
        >
          <ItpBox alignItems="center">
            <ItpText
              :text="term.name"
              :strikethrough="isTermStale(term)"
              class="fw-bold"
            >
            </ItpText>
            <ItpIcon
              :title="gettext('Locked')"
              name="fa-lock"
              class="px-3"
              v-if="term.locked"
            >
            </ItpIcon>
            <div>
              {{ date(term.valid_from) }}
              <span v-if="term.valid_from && term.valid_to">-</span>
              {{ date(term.valid_to) }}
            </div>
            <ItpBox>
              <ItpText>
                <ItpText class="px-3">
                  <span v-if="term.quantity != null">
                    {{ number(term.quantity) }} x
                  </span>
                  <span v-if="term.amount != null">
                    {{ number(term.amount) }}
                  </span>
                  <span v-if="term.currency">
                    {{ term.currency }}
                  </span>
                  <span v-if="term.unit"> / {{ term.unit }} </span>
                </ItpText>
              </ItpText>
              <ItpText :text="term.notes" pre v-if="term.notes"> </ItpText>
            </ItpBox>
            <ItpButton
              icon="fa-pen"
              :tooltip="gettext('Edit')"
              variant="light"
              size="sm"
              class="ms-auto"
              v-if="!readonly"
              @click.prevent.stop="
                evh_3577443900630089_clickPreventStop($event, { term })
              "
            >
            </ItpButton>
          </ItpBox>
          <ItpBox>
            <ItpText :text="term.description" pre> </ItpText>
          </ItpBox>
        </BListGroupItem>
      </BListGroup>
    </ItpWidgetCard>
  </ItpUsercontrol>
</template>
